'use client';

import SettingConfig from '@module/config/setting';
import useGetLoginInfoQuery from '@module/helpers/auth/useGetLoginInfoQuery';
import { aspCond } from '@module/helpers/conditions/condition';
import useIsDarkModeStore from '@module/stores/useIsDarkModeStore';
import useModalStore from '@module/stores/useModalStore';
import useIsMounted from '@module/utils/useIsMounted';
import BaseSVG from '@pc/components/image/BaseSVG';
import BaseLottie from '@pc/components/lottie/BaseLottie';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

type MappingPath = 'live' | 'ranking' | 'pick' | 'vModePick' | 'vModeRanking' | 'vModeLive';

const getPath = (mappingPath: MappingPath) => {
  switch (mappingPath) {
    case 'pick':
      return `/${mappingPath}`;
    default:
      return `/${mappingPath}`;
  }
};

const getSearchParams = (mappingPath: MappingPath) => {
  switch (mappingPath) {
    case 'ranking':
      return '/rankingPersonalBJ';
    default:
      return '';
  }
};

interface Props {
  children: React.ReactNode;
  mappingPath: MappingPath;
  disabled?: boolean;
}

const imgMapping = {
  live: {
    svg: '/sidebar/web_live.svg',
    json: '/sidebar/menu_live_on.json',
    darkJson: '/sidebar/dark_web_live_on.json',
  },
  ranking: {
    svg: '/sidebar/web_ranking.svg',
    json: '/sidebar/menu_ranking_on.json',
    darkJson: '/sidebar/dark_web_ranking_on.json',
  },
  pick: {
    svg: '/sidebar/web_pick.svg',
    json: '/sidebar/menu_pick_on.json',
    darkJson: '/sidebar/dark_web_pick_on.json',
  },
  vModeLive: {
    svg: '/sidebar/web_live.svg',
    json: '/sidebar/menu_live_on.json',
    darkJson: '/sidebar/dark_web_live_on.json',
  },
  vModeRanking: {
    svg: '/sidebar/web_ranking.svg',
    json: '/sidebar/menu_ranking_on.json',
    darkJson: '/sidebar/dark_web_ranking_on.json',
  },
  vModePick: {
    svg: '/sidebar/web_pick.svg',
    json: '/sidebar/menu_pick_on.json',
    darkJson: '/sidebar/dark_web_pick_on.json',
  },
};

function ActiveLottieLink({ mappingPath, disabled = false, children }: Props) {
  const { isLogin } = useGetLoginInfoQuery();
  const pushModal = useModalStore((state) => state.pushModal);
  const pathname = usePathname();

  const path = getPath(mappingPath);
  const searchParams = getSearchParams(mappingPath);

  const [enabledHover, setEnabledHover] = useState(false);
  const { isDark } = useIsDarkModeStore();

  const mounted = useIsMounted();
  const darkThemeLottie = `${SettingConfig.S3_BUCKET_URL}/json/lottie${imgMapping[mappingPath].darkJson}`;
  const lightThemeLottie = `${SettingConfig.S3_BUCKET_URL}/json/lottie${imgMapping[mappingPath].json}`;
  const chosenLottie = isDark ? darkThemeLottie : lightThemeLottie;

  return (
    <>
      {mappingPath !== 'pick' && (
        <Link
          prefetch={false}
          className='flex h-[50px] w-full items-center rounded-[10px] px-[17px] py-[11px] text-center text-[15px] hover:bg-colorBg01'
          href={`${path.includes('vMode') ? '' : `${path}${searchParams}`}`}
          rel='noreferrer'
          onMouseEnter={() => {
            setEnabledHover(true);
          }}
          onMouseLeave={() => {
            setEnabledHover(false);
          }}
          onClick={(e) => {
            if (path.includes('vMode')) {
              pushModal({
                type: 'AUTH_MODAL',
                id: 'ActiveLottieLink',
                options: {
                  tab: 'register',
                },
              });
            }
            if (disabled) {
              e.preventDefault();
            }
          }}
        >
          <div className='outlimne mr-[25px] h-[22px] w-[22px]'>
            {pathname.includes(path) ||
            enabledHover ||
            !chosenLottie ||
            (mappingPath === 'live' && pathname.includes('play')) ? (
              <BaseLottie
                src={chosenLottie}
                loop={0}
                fallback={
                  <BaseSVG
                    src={`${SettingConfig.S3_BUCKET_URL}/img${imgMapping[mappingPath].svg}`}
                    height={22}
                    width={22}
                  />
                }
              />
            ) : (
              <BaseSVG
                src={`${SettingConfig.S3_BUCKET_URL}/img${imgMapping[mappingPath].svg}`}
                height={22}
                width={22}
              />
            )}
          </div>
          <span className='mt-0 text-[15px]'>{children}</span>
        </Link>
      )}
      {mounted && isLogin && mappingPath === 'pick' && (
        <Link
          prefetch={false}
          className='flex h-[50px] w-full items-center rounded-[10px] px-[17px] py-[11px] text-center text-[15px] hover:bg-colorBg01'
          href={path}
          rel='noreferrer'
          onMouseEnter={() => {
            setEnabledHover(true);
          }}
          onMouseLeave={() => {
            setEnabledHover(false);
          }}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
            }
          }}
        >
          <div className='mr-[25px] h-[22px] w-[22px]'>
            {!pathname.includes(path) && !enabledHover ? (
              <BaseSVG
                src={`${SettingConfig.S3_BUCKET_URL}/img${imgMapping[mappingPath].svg}`}
                height={22}
                width={22}
              />
            ) : (
              <BaseLottie
                src={chosenLottie}
                loop={0}
                fallback={
                  <BaseSVG
                    src={`${SettingConfig.S3_BUCKET_URL}/img${imgMapping[mappingPath].svg}`}
                    height={22}
                    width={22}
                  />
                }
              />
            )}
          </div>
          <span className='mt-0 text-[15px]'>{children}</span>
        </Link>
      )}
      {(!mounted || !isLogin) && mappingPath === 'pick' && (
        <button
          type='button'
          className='flex h-[50px] w-full items-center rounded-[10px] px-[17px] py-[11px] text-center text-[15px] hover:bg-colorBg01'
          onClick={() => {
            if (aspCond.if('new-asp')) {
              SettingConfig.aspUtils?.goLogin();
              return;
            }
            pushModal({
              type: 'AUTH_MODAL',
              id: 'ActiveLottieLink',
            });
          }}
          onMouseEnter={() => {
            setEnabledHover(true);
          }}
          onMouseLeave={() => {
            setEnabledHover(false);
          }}
          disabled={disabled}
        >
          <div className='mr-[25px] h-[22px] w-[22px]'>
            {!pathname.includes(path) && !enabledHover ? (
              <BaseSVG src={`${SettingConfig.S3_BUCKET_URL}/img/sidebar/web_pick.svg`} height={22} width={22} />
            ) : (
              <BaseLottie src={chosenLottie} loop={0} />
            )}
          </div>
          <span className='mt-0 text-[15px]'>{children}</span>
        </button>
      )}
    </>
  );
}

export default ActiveLottieLink;
